import Phaser from 'phaser';

import {
	CORRECT_ARROW_TEXTURE_KEY,
	CORRECT_ICON_TEXTURE_KEY,
	INCORRECT_ICON_TEXTURE_KEY,
} from '@/features/laneChooser/game/objects/AnswerBoxView';
import { getLaneChooserThemeSceneKey } from '@/features/laneChooser/game/scenes/LaneChooserThemeScene';
import { normalizeSvgAssetUrl } from '@/features/laneChooser/game/utils/svgAssetUrl';
import { getSvgTextureSize } from '@/features/laneChooser/game/utils/svgSizing';

import correctArrowUrl from '@/features/laneChooser/assets/images/correct-arrow.svg?url';
import correctIconUrl from '@/features/laneChooser/assets/images/correct.svg?url';
import incorrectIconUrl from '@/features/laneChooser/assets/images/incorrect.svg?url';

import type {
	LaneChooserAnswerAsset,
	LaneChooserGameTheme,
	LaneChooserSceneAsset,
} from '@/features/laneChooser/game/types';

function isStaticAnswerAsset(
	asset: LaneChooserAnswerAsset,
): asset is Extract<LaneChooserAnswerAsset, { assetUrl: string }> {
	return 'assetUrl' in asset;
}

function loadSvgTexture(
	scene: Phaser.Scene,
	textureKey: string,
	assetUrl: string,
	assetSize: LaneChooserSceneAsset['assetSize'],
) {
	scene.load.svg(textureKey, normalizeSvgAssetUrl(assetUrl), getSvgTextureSize(scene, assetSize));
}

export class BootScene extends Phaser.Scene {
	private readonly themeConfig: LaneChooserGameTheme;

	constructor(themeConfig: LaneChooserGameTheme) {
		super('laneChooserBoot');
		this.themeConfig = themeConfig;
	}

	preload() {
		loadSvgTexture(
			this,
			this.themeConfig.textureKeys.topPanel,
			this.themeConfig.assetUrls.topPanel,
			this.themeConfig.assetSizes.topPanel,
		);
		loadSvgTexture(
			this,
			this.themeConfig.textureKeys.bottomPanel,
			this.themeConfig.assetUrls.bottomPanel,
			this.themeConfig.assetSizes.bottomPanel,
		);
		if (this.themeConfig.foreground) {
			loadSvgTexture(
				this,
				this.themeConfig.foreground.textureKey,
				this.themeConfig.foreground.assetUrl,
				this.themeConfig.foreground.assetSize,
			);
		}
		this.themeConfig.sky.clouds.forEach((cloud) => {
			loadSvgTexture(this, cloud.textureKey, cloud.assetUrl, cloud.assetSize);
		});
		this.themeConfig.sideElements.assets.forEach((asset) => {
			loadSvgTexture(this, asset.textureKey, asset.assetUrl, asset.assetSize);
		});
		Object.values(this.themeConfig.answers.animations).forEach((answerAsset) => {
			if (answerAsset && isStaticAnswerAsset(answerAsset)) {
				loadSvgTexture(this, answerAsset.textureKey, answerAsset.assetUrl, answerAsset.assetSize);
			}
		});
		loadSvgTexture(this, CORRECT_ICON_TEXTURE_KEY, correctIconUrl, {
			width: 1024,
			height: 1024,
		});
		loadSvgTexture(this, INCORRECT_ICON_TEXTURE_KEY, incorrectIconUrl, {
			width: 1024,
			height: 1024,
		});
		loadSvgTexture(this, CORRECT_ARROW_TEXTURE_KEY, correctArrowUrl, {
			width: 72,
			height: 72,
		});
		loadSvgTexture(
			this,
			this.themeConfig.player.jump.textureKey,
			this.themeConfig.player.jump.assetUrl,
			this.themeConfig.player.jump.assetSize,
		);
	}

	create() {
		this.scene.start(getLaneChooserThemeSceneKey(this.themeConfig.key));
	}
}
